import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import LogoutButton from "./LogoutButton";
import logo from "../assets/images/White_Version.png";
import { Shield, DollarSign, Percent, Star } from "lucide-react"
import { getAuthCookie } from "../utils/authCookies";
/**
 * Brokerage TeamMember Sidebar (dynamic)
 * Pulls module codes from /permission-scope/modules and renders in this order:
 * Offices (static)
 * Agents (dynamic)
 * Suppliers (dynamic)
 * Inventory (dynamic) -> Office Inventory, Agent Inventory, Categories, Brands
 * Orders (dynamic)
 * Jobs (dynamic) -> Installations, Removal Requests, Active Installations
 * Reports (static, with children)
 * Addresses (static)
 * Profile (static)
 * Logout (static)
 */

type ModuleCode =
  | "AGENTS"
  | "SUPPLIERS"
  | "ORDERS"
  | "OFFICE_INVENTORY"
  | "AGENT_INVENTORY"
  | "CATEGORIES"
  | "BRANDS"
  | "INSTALLATION_ORDERS"
  | "REMOVAL_ORDERS"
  | "ACTIVE_INSTALLATIONS"
  | "BROKERAGE_AGENTS"
  | "REQUESTS"
  | "BROKERAGE_CHANGE_REQUESTS"
  | "DASH_BOARD"
  | "BATCH_PAYMENTS"
  | "REPORTS"
  | "PROJECTS"
  | (string & {});

type ItemMeta = { label: string; path: string; icon?: string };

// Helper function to create single map based on baseRoute
const createSingleMap = (baseRoute: string): Record<string, ItemMeta> => ({
  BROKERAGE_AGENTS: { label: "Agents", path: `${baseRoute}/agents`, icon: "ri-user-line" },
  SUPPLIERS: { label: "Suppliers", path: `${baseRoute}/suppliers`, icon: "ri-store-2-line" },
  ORDERS: { label: "Orders", path: `${baseRoute}/orders`, icon: "bx bx-cart-alt" },
  PROJECTS: { label: "Design Requests", path: `${baseRoute}/design-requests`, icon: "ri-folder-chart-line" },
  BATCH_PAYMENTS: { label: "Batch Payments", path: `${baseRoute}/batchPayments`, icon: "ri-file-text-line" },
});

// Helper function to create inventory children based on baseRoute
const createInventoryChildren = (baseRoute: string): Record<string, ItemMeta> => ({
  OFFICE_INVENTORY: { label: "Office Inventory", path: `${baseRoute}/officeInventory` },
  AGENT_INVENTORY: { label: "Agent Inventory", path: `${baseRoute}/agentInventory` },
  CATEGORIES: { label: "Categories", path: `${baseRoute}/categories` },
  BRANDS: { label: "Brands", path: `${baseRoute}/brands` },
});

// Helper function to create jobs children based on baseRoute
const createJobsChildren = (baseRoute: string): Record<string, ItemMeta> => ({
  INSTALLATION_ORDERS: { label: "Installations", path: `${baseRoute}/jobs/installations` },
  REMOVAL_ORDERS: { label: "Removal Requests", path: `${baseRoute}/jobs/removalRequests` },
  ACTIVE_INSTALLATIONS: { label: "Active Installations", path: `${baseRoute}/active-installations` },
});

// Helper function to create reports children based on baseRoute
const createReportsChildren = (baseRoute: string): ItemMeta[] => [
  { label: "Installation Summary Report", path: `${baseRoute}/installationSummaryReport` },
  { label: "Removal Summary Report", path: `${baseRoute}/removalSummaryReport` },
  { label: "SalesOrder Summary Report", path: `${baseRoute}/salesOrderReport` },
];

export default function TeammemberSidebar() {
  const location = useLocation();
  const [codes, setCodes] = useState<ModuleCode[]>([]);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const token = getAuthCookie("sessionToken") || "";

  // Determine the base route based on current location
  const isRealtor = location.pathname.includes('/realtor');
  const baseRoute = isRealtor ? '/realtor' : '/teammember';

  // Create dynamic path maps based on baseRoute
  const SINGLE_MAP = useMemo(() => createSingleMap(baseRoute), [baseRoute]);
  const INVENTORY_CHILDREN = useMemo(() => createInventoryChildren(baseRoute), [baseRoute]);
  const JOBS_CHILDREN = useMemo(() => createJobsChildren(baseRoute), [baseRoute]);
  const REPORTS_CHILDREN = useMemo(() => createReportsChildren(baseRoute), [baseRoute]);

  // fetch allowed module codes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/permission-scope/modules`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        // console.log("Fetched modules:", data);
        if (!Array.isArray(data)) throw new Error("Invalid response");
        if (!cancelled) {
          setCodes((data.filter((x: any) => typeof x === "string") as string[]) as ModuleCode[]);
        }
      } catch (e) {
        console.error("Failed to fetch modules:", e);
        if (!cancelled) setCodes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (window.innerWidth < 992) {
      document.body.classList.remove("vertical-sidebar-enable", "sidebar-enable");
    }
  }, [location.pathname]);

  const has = (code: ModuleCode) => codes.includes(code);
  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const inventoryChildren = useMemo(
    () =>
      (Object.keys(INVENTORY_CHILDREN) as ModuleCode[])
        .filter((code) => has(code))
        .map((code) => INVENTORY_CHILDREN[code]),
    [codes, INVENTORY_CHILDREN]
  );

  const jobsChildren = useMemo(
    () =>
      (Object.keys(JOBS_CHILDREN) as ModuleCode[])
        .filter((code) => has(code))
        .map((code) => JOBS_CHILDREN[code]),
    [codes, JOBS_CHILDREN]
  );

  return (
    <div className="app-menu navbar-menu">
      {/* ✅ MOBILE top strip (logo + close), visible on ≤767px */}
      <div className="mobile-sidebar-header d-md-none d-flex align-items-center gap-2 px-3 py-2">
        <Link to= {`${baseRoute}/welcome`}  className="d-inline-flex align-items-center text-decoration-none">
          <img src={logo} alt="Team Member" height={80} />
        </Link>
        <button
          type="button"
          aria-label="Close sidebar"
          className="btn btn-sm btn-icon btn-ghost-light ms-auto mt-1"
          onClick={() => {
            document.body.classList.remove("vertical-sidebar-enable", "sidebar-enable");
          }}
        >
          <i className="ri-close-line fs-4"></i>
        </button>
      </div>
      {/* Brand */}
      <div className="navbar-brand-box d-none d-md-block">
        <Link to="/" className="logo logo-dark">
          <span className="logo-sm"><h2>REALESY</h2></span>
          <span className="logo-lg"><h2>REALESY</h2></span>
        </Link>
        <Link to="/" className="logo logo-light">
          <span className="logo-sm"><h2>REALESY</h2></span>
          <span className="logo-lg"><h2>REALESY</h2></span>
        </Link>
        <button type="button" className="btn btn-sm p-0 fs-20 header-item float-end btn-vertical-sm-hover" id="vertical-hover">
          <i className="ri-record-circle-line"></i>
        </button>
      </div>

      {/* User Dropdown */}
      <div className="dropdown sidebar-user m-1 rounded">
        <button
          type="button"
          className="btn material-shadow-none"
          id="page-header-user-dropdown"
          data-bs-toggle="dropdown"
          aria-haspopup="true"
          aria-expanded="false"
        >
          <span className="d-flex align-items-center gap-2">
            <img className="rounded header-profile-user" src="/assets/images/users/avatar-1.jpg" alt="Header Avatar" />
            <span className="text-start">
              <span className="d-block fw-medium sidebar-user-name-text">Anna Adame</span>
              <span className="d-block fs-14 sidebar-user-name-sub-text">
                <i className="ri-circle-fill fs-10 text-success align-baseline"></i>{" "}
                <span className="align-middle">Online</span>
              </span>
            </span>
          </span>
        </button>
        <div className="dropdown-menu dropdown-menu-end">
          <h6 className="dropdown-header">Welcome Anna!</h6>
          <Link to={`${baseRoute}/profile`} className="dropdown-item">
            <i className="mdi mdi-account-circle text-muted fs-16 align-middle me-1"></i>
            <span className="align-middle">Profile</span>
          </Link>
          <LogoutButton />
        </div>
      </div>

      {/* Sidebar Navigation */}
      <div id="scrollbar">
        <div className="container-fluid">
          <ul className="navbar-nav" id="navbar-nav">
            {/* Dashboard (permission-based) */}
            {/* <li className="nav-item">
                <Link to={`${baseRoute}/welcome`} className={`nav-link menu-link ${isActive(`${baseRoute}/wellcome`) ? "active" : ""}`}>
                    <Star size={28} color="#ffffff" className="ms-1" /> <span>Welcome</span>
                </Link>
              </li> */}
            {has("DASH_BOARD") && (
              <li className="nav-item">
                <Link to={`${baseRoute}/dashboard`} className={`nav-link menu-link ${isActive(`${baseRoute}/dashboard`) ? "active" : ""}`}>
                  <i className="ri-dashboard-fill"></i> <span>Dashboard</span>
                </Link>
              </li>
            )}

            {/* Offices (Static) */}
            <li className="nav-item">
              <Link to={`${baseRoute}/offices`} className={`nav-link menu-link ${isActive(`${baseRoute}/offices`) ? "active" : ""}`}>
                <i className="ri-building-2-line"></i> <span>Offices</span>
              </Link>
            </li>

            {/* Agents (Dynamic) */}
            {has("BROKERAGE_AGENTS") && (
              <li className="nav-item">
                <Link to={SINGLE_MAP.BROKERAGE_AGENTS.path} className={`nav-link menu-link ${isActive(SINGLE_MAP.BROKERAGE_AGENTS.path) ? "active" : ""}`}>
                  <i className={SINGLE_MAP.BROKERAGE_AGENTS.icon!}></i> <span>{SINGLE_MAP.BROKERAGE_AGENTS.label}</span>
                </Link>
              </li>
            )}

            {has("BROKERAGE_CHANGE_REQUESTS") && (
              <li className="nav-item">
                <Link
                  to={`${baseRoute}/vendorRequests`}
                  className={`nav-link menu-link ${isActive(`${baseRoute}/vendorRequests`) ? "active" : ""}`}
                >
                  <i className="ri-file-list-3-line"></i> <span>Vendor requests</span>
                </Link>
              </li>
            )}

            {/* Suppliers (Dynamic) */}
            {has("SUPPLIERS") && (
              <li className="nav-item">
                <Link to={SINGLE_MAP.SUPPLIERS.path} className={`nav-link menu-link ${isActive(SINGLE_MAP.SUPPLIERS.path) ? "active" : ""}`}>
                  <i className={SINGLE_MAP.SUPPLIERS.icon!}></i> <span>{SINGLE_MAP.SUPPLIERS.label}</span>
                </Link>
              </li>
            )}

            {/* Inventory (Dynamic group) */}
            {inventoryChildren.length > 0 && (
              <li className="nav-item">
                <a
                  className="nav-link menu-link"
                  onClick={() => setOpenMenu(openMenu === "inventoryMenu" ? null : "inventoryMenu")}
                  href="#inventoryMenu"
                  data-bs-toggle="collapse"
                  role="button"
                  aria-expanded={openMenu === "inventoryMenu" ? "true" : "false"}
                  aria-controls="inventoryMenu"
                >
                  <i className="ri-rocket-line"></i> <span>Inventory</span>
                </a>
                <div className={`collapse menu-dropdown ${openMenu === "inventoryMenu" ? "show" : ""}`} id="inventoryMenu">
                  <ul className="nav nav-sm flex-column">
                    {inventoryChildren.map((child) => (
                      <li className="nav-item" key={child.path}>
                        <Link to={child.path} className={`nav-link ${isActive(child.path) ? "active" : ""}`}>
                          {child.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            )}

            {/* Orders (Dynamic) */}
            {has("ORDERS") && (
              <li className="nav-item">
                <Link to={SINGLE_MAP.ORDERS.path} className={`nav-link menu-link ${isActive(SINGLE_MAP.ORDERS.path) ? "active" : ""}`}>
                  <i className={SINGLE_MAP.ORDERS.icon!}></i> <span>{SINGLE_MAP.ORDERS.label}</span>
                </Link>
              </li>
            )}

            {/* Design Requests (Dynamic) */}
            {has("PROJECTS") && (
              <li className="nav-item">
                <Link to={SINGLE_MAP.PROJECTS.path} className={`nav-link menu-link ${isActive(SINGLE_MAP.PROJECTS.path) ? "active" : ""}`}>
                  <i className={SINGLE_MAP.PROJECTS.icon!}></i> <span>{SINGLE_MAP.PROJECTS.label}</span>
                </Link>
              </li>
            )}

            {/* Jobs (Dynamic group) */}
            {jobsChildren.length > 0 && (
              <li className="nav-item">
                <a
                  className="nav-link menu-link"
                  onClick={() => setOpenMenu(openMenu === "jobsMenu" ? null : "jobsMenu")}
                  href="#jobsMenu"
                  data-bs-toggle="collapse"
                  role="button"
                  aria-expanded={openMenu === "jobsMenu" ? "true" : "false"}
                  aria-controls="jobsMenu"
                >
                  <i className="ri-briefcase-fill"></i> <span>Jobs</span>
                </a>
                <div className={`collapse menu-dropdown ${openMenu === "jobsMenu" ? "show" : ""}`} id="jobsMenu">
                  <ul className="nav nav-sm flex-column">
                    {jobsChildren.map((child) => (
                      <li className="nav-item" key={child.path}>
                        <Link to={child.path} className={`nav-link ${isActive(child.path) ? "active" : ""}`}>
                          {child.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            )}

            {/* Batch Payments (dynamic) */}
            {has("BATCH_PAYMENTS") && (
              <li className="nav-item">
                <Link to={SINGLE_MAP.BATCH_PAYMENTS.path} className={`nav-link menu-link ${isActive(SINGLE_MAP.BATCH_PAYMENTS.path) ? "active" : ""}`}>
                  <i className={SINGLE_MAP.BATCH_PAYMENTS.icon!}></i> <span>{SINGLE_MAP.BATCH_PAYMENTS.label}</span>
                </Link>
              </li>
            )}

            {/* Reports (permission-based group) */}
            {has("REPORTS") && (
              <li className="nav-item">
                <a
                  className="nav-link menu-link"
                  onClick={() => setOpenMenu(openMenu === "reportsMenu" ? null : "reportsMenu")}
                  href="#reportsMenu"
                  data-bs-toggle="collapse"
                  role="button"
                  aria-expanded={openMenu === "reportsMenu" ? "true" : "false"}
                  aria-controls="reportsMenu"
                >
                  <i className="ri-bar-chart-2-fill"></i> <span>Reports</span>
                </a>
                <div className={`collapse menu-dropdown ${openMenu === "reportsMenu" ? "show" : ""}`} id="reportsMenu">
                  <ul className="nav nav-sm flex-column">
                    {REPORTS_CHILDREN.map((child) => (
                      <li className="nav-item" key={child.path}>
                        <Link to={child.path} className={`nav-link ${isActive(child.path) ? "active" : ""}`}>
                          {child.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            )}

            {/* Addresses (Static) */}
            {/* <li className="nav-item">
              <Link to="/teammember/addresses" className={`nav-link menu-link ${isActive("/teammember/addresses") ? "active" : ""}`}>
                <i className="ri-user-line"></i> <span>Addresses</span>
              </Link>
            </li> */}

            {/* Profile (Static) */}
            <li className="nav-item">
              <Link to={`${baseRoute}/profile`} className={`nav-link menu-link ${isActive(`${baseRoute}/profile`) ? "active" : ""}`}>
                <i className="ri-user-line"></i> <span>Profile</span>
              </Link>
            </li>

            {/* Logout (Static) */}
            <li className="nav-item">
              <LogoutButton />
            </li>
          </ul>
        </div>
      </div>

      <div
        className="sidebar-background"
        onClick={() => {
          document.body.classList.remove("vertical-sidebar-enable", "sidebar-enable");
        }}
      ></div>
    </div>
  );
}